/** @module store */

import { createStore } from 'vuex'

import amperixes from '@/store/modules/amperixes'
import api from '@/store/modules/api'
import branding from '@/store/modules/branding'
import logger from '@/logger'
import notifications from '@/store/modules/notifications'
import communities from '@/store/modules/communities'
import emsCurrentStatus from '@/store/modules/ems_current_status'
import emsMetaInfos from '@/store/modules/ems_meta_infos'
import energyManagementSystems from '@/store/modules/energy_management_systems'
import energyManagementSystemGroups from '@/store/modules/energy_management_system_groups'
import user from '@/store/modules/user'
import users from '@/store/modules/users'
import adminUsersManagement from '@/store/modules/admin/users_management'
import amperixCommissionInfos from '@/store/modules/amperix_commission_infos'
import datetimepicker from '@/store/modules/datetimepicker'
import measurements from '@/store/modules/measurements'
import boards from '@/store/modules/boards'
import emsVariableStores from '@/store/modules/ems_variable_stores'
import chaiScript from '@/store/modules/chai_script'
import reporters from '@/store/modules/reporters'
import meterExchangePairs from '@/store/modules/meter_exchange_pairs'
import targetAccessPermissions from '@/store/modules/target_access_permissions'
import logEvents from '@/store/modules/log_events'
import customEvents from '@/store/modules/custom_events'
import energyManager from '@/store/modules/energy_manager'

/**
 * Factory to create a [Vuex store]{@link https://vuex.vuejs.org}
 *
 * @function
 *
 * @param {object} args are Vuex store [constructor options]{@link https://vuex.vuejs.org/api/#vuex-store-constructor-options}
 * @param {boolean} devStrict disables strict mode on development for performance reasons {@link https://vuex.vuejs.org/guide/strict.html}
 * @param {object} opts
 * @param {boolean} opts.withDefaults (optional) allows to enable/disable if default state/getters/mutations/actions/modules should be included
 *
 * @return {Vuex.Store} a Vuex Store
 */
export function buildStore(
  { state, getters, mutations, actions, modules = {}, devStrict = true },
  { withDefaults = true } = {}
) {
  if (withDefaults) {
    Object.assign(modules, { api, branding, notifications, user })
  }
  return createStore({
    state,
    getters,
    mutations,
    actions,
    modules,
    strict: process.env.NODE_ENV !== 'production' && devStrict
  })
}

export function createChartStore() {
  const modules = {
    datetimepicker,
    measurements
  }

  return buildStore({ modules })
}

export function createSensorReportStore() {
  const modules = {
    datetimepicker,
    energyManagementSystems,
    measurements
  }

  return buildStore({ modules })
}

export function createSettingsDevicesStore() {
  const modules = {
    energyManagementSystems,
    measurements
  }

  return buildStore({ modules })
}

export function createSettingsEmsVariableStore() {
  const modules = {
    energyManagementSystems,
    emsVariableStores,
    energyManager,
    measurements
  }

  return buildStore({ modules })
}

export function createSettingsChargepointParametrizationStore() {
  const modules = {
    chaiScript,
    energyManagementSystems,
    emsVariableStores,
    measurements
  }

  return buildStore({ modules })
}

export function createAdminUsersManagementStore() {
  const modules = {
    adminUsersManagement,
    amperixCommissionInfos,
    communities,
    boards,
    energyManagementSystems,
    energyManagementSystemGroups,
    users
  }

  return buildStore({ modules })
}

export function createEmsManagementStore() {
  const modules = {
    adminUsersManagement,
    energyManagementSystems,
    energyManagementSystemGroups,
    measurements,
    targetAccessPermissions,
    users
  }

  return buildStore({ modules })
}

export function createNotificationsManagementStore() {
  const modules = {
    energyManagementSystemGroups,
    energyManagementSystems,
    measurements,
    reporters,
    users
  }

  return buildStore({ modules })
}

export function createDashboardStore() {
  const modules = {
    amperixes,
    communities,
    emsMetaInfos,
    energyManagementSystems,
    energyManagementSystemGroups,
    measurements,
    boards,
    logEvents
  }
  // devStrict set to false for DashboardStore to enable needed update performance for live updates
  return buildStore({ modules, devStrict: false })
}

export function createMeasurementsAndMeterExchangePairsStore() {
  const modules = {
    energyManagementSystems,
    energyManagementSystemGroups,
    measurements,
    meterExchangePairs
  }

  return buildStore({ modules })
}

export function createTechnicianIndexStore() {
  const modules = {
    amperixes,
    emsCurrentStatus,
    energyManagementSystems,
    logEvents
  }

  return buildStore({ modules })
}

export function createCustomEventsStore() {
  const modules = {
    customEvents,
    energyManagementSystems
  }

  return buildStore({ modules })
}

export function createTechnicianAdminClaimStore() {
  const modules = {
    amperixes,
    energyManagementSystems
  }

  return buildStore({ modules })
}

/**
 * Checks the HTML DOM for branding specific data.
 * Stores those.
 *
 * Checks if store has module 'branding'.
 *
 * @function
 *
 * @param {Vuex.Store} store
 *
 */
export function initBrandingStore(store) {
  if (!store.hasModule('branding')) {
    logger.info('Skipped init branding store, because module is not present.')
    return
  }

  const $main = document.getElementById('main')
  try {
    store.commit('branding/SET_ID', $main.dataset.branding, { root: true })
    store.commit('branding/SET_APX_LABEL', $main.dataset.apxLabel, { root: true })
    store.commit('branding/SET_MPG_LABEL', $main.dataset.mpgLabel, { root: true })
    logger.info('Finished init branding store.')
  } catch (err) {
    logger.error('Failed init branding store.')
    logger.error(err)
  }
}
